import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ClearBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/clear-background',
			name: 'Clear Background Online — Make Photo Background Clear | Lift',
			description:
				'Clear photo background instantly with AI. No watermark. Upload photo, make clear backgrounds, and create transparent PNGs in seconds. Try now!',
			url: 'https://liftapp.ai/clear-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/clear-background#product' },
				{ '@id': 'https://liftapp.ai/clear-background#howto' },
				{ '@id': 'https://liftapp.ai/clear-background#benefits' },
				{ '@id': 'https://liftapp.ai/clear-background#faq' },
				{ '@id': 'https://liftapp.ai/clear-background#why-lift' },
				{ '@id': 'https://liftapp.ai/clear-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/clear-background#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/clear-background#product',
			name: 'Clear Background Maker Online',
			description:
				'Make background clear and transparent in seconds. Get a professional PNG with clear background, no watermarks, batch up to 20 images.',
			url: 'https://liftapp.ai/clear-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/clear-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift clears the background automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/clear-background#benefits',
			name: 'Who can benefit from Clear Background Maker?',
			description:
				"Lift's AI Clear Background Maker is useful for professionals, creators, and businesses who want clean, transparent PNG images for e-commerce, social media, marketing, or personal use.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'E-commerce Sellers — Clear photo backgrounds to create professional product images for your online store. Make background clear on up to 20 photos at once to build clean catalogs that increase sales and conversions',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content Creators — Clear background from images to design stunning thumbnails, Instagram stories, and TikTok content. Create eye-catching collages and memes with transparent PNGs without expensive editing software',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Designers & Freelancers — Clear background online instantly to speed up your design workflow and deliver projects faster. Get pixel-perfect cutouts with transparent backgrounds to impress clients and take on more work',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small Business Owners — Make clear backgrounds for marketing materials, flyers, presentations, and social media ads. Create professional visuals in minutes without hiring a designer or learning complex software',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Clear background for school projects, presentations, and creative assignments. Make PNG clear background images for posters, reports, and digital portfolios that stand out',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'PNG clear background',
				description:
					'Example of how Lift Clear Background Maker creates clear transparent PNG images for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/clear-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Background Clearer feature?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Background Clearer is a premium online tool that instantly clears photo backgrounds with pixel-perfect precision — ideal for product photos, profile pictures, or graphic design.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Clear Background Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's clear background tool. Our AI automatically detects the subject and clears the background in seconds — no photo editing experience needed. Download up to 20 images with transparent PNG backgrounds.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Clear Background Remover uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/clear-background#why-lift',
			name: "Why Lift's AI Clear Background Maker stands out",
			description:
				"Lift's Clear Background Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Clear background maker',
				description:
					"Comparison of Lift's AI Clear Background Maker benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/clear-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/clear-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Clear Background Maker',
					item: 'https://liftapp.ai/clear-background',
				},
			],
		},
	],
};
